import type { Builder } from "../../types.js";

export type HierarchyV2ListItem = {
	id: string;
	name: string;
	reportingType: "IncomeStatement" | "BalanceSheet";
	status: "Draft" | "Published";
	isDefault: boolean;
	validationFromDate: string;
	currentVersion: number;
	createdAt: string;
	updatedAt: string;
};

export type HierarchyBatchOperation =
	| {
			op: "createGroup";
			tempId: string;
			name: string;
			parentId?: string | null;
			sortOrder: number;
			type: "Sum" | "Group";
			functionCode?: string | null;
	  }
	| {
			op: "updateGroup";
			groupId: string;
			name?: string;
			sortOrder?: number;
			type?: "Sum" | "Group";
			functionCode?: string | null;
	  }
	| {
			op: "moveGroup";
			groupId: string;
			parentId: string | null;
			sortOrder: number;
	  }
	| { op: "deleteGroup"; groupId: string }
	| {
			op: "createFormula";
			groupId: string;
			accountFormula: string;
			costCenterFormula?: string | null;
			sortOrder: number;
	  }
	| {
			op: "updateFormula";
			groupId: string;
			formulaId: string;
			accountFormula?: string;
			costCenterFormula?: string | null;
			sortOrder?: number;
	  }
	| { op: "deleteFormula"; groupId: string; formulaId: string }
	| {
			op: "updateHierarchy";
			name?: string;
			excludedAccountNumbers?: string[];
	  };

export type HierarchyBatchResponse = {
	hierarchy: HierarchyV2Detail;
	versionNumber: number | null;
};

export type HierarchyVersionKind = "added" | "removed" | "changed" | "moved";

export type HierarchyVersionSummaryEntry = {
	key: string;
	kind: HierarchyVersionKind;
	target: "hierarchy" | "group" | "formula";
	ref: string | null;
	path: string[];
	params: Record<string, unknown>;
};

export type HierarchyVersionCounts = {
	added: number;
	removed: number;
	changed: number;
	moved: number;
};

export type HierarchyVersionAuthor = {
	name: string | null;
	email: string | null;
};

export type HierarchyVersionListItem = {
	versionNumber: number;
	titleKey: string;
	author: HierarchyVersionAuthor | null;
	createdAt: string;
	revertedFromVersion: number | null;
	counts: HierarchyVersionCounts;
	summary?: {
		entries: HierarchyVersionSummaryEntry[];
		truncated: boolean;
		totalEntries: number;
	};
};

export type HierarchyVersionsResponse = {
	skipped: number;
	cursor: string | null;
	items: HierarchyVersionListItem[];
};

export type FormulaV2Response = {
	id: string;
	accountFormula: string;
	costCenterFormula: string | null;
	sortOrder: number;
	createdAt: string;
	updatedAt: string;
};

export type GroupV2Response = {
	id: string;
	name: string;
	parentId: string | null;
	sortOrder: number;
	type: "Sum" | "Group";
	functionCode: string | null;
	formulas: FormulaV2Response[];
	nodes: GroupV2Response[];
	createdAt: string;
	updatedAt: string;
};

export type HierarchyV2Detail = HierarchyV2ListItem & {
	nodes: GroupV2Response[];
	excludedAccountNumbers: string[];
};

export type BulkFormulaInput = {
	accountFormula: string;
	costCenterFormula?: string | null;
	sortOrder: number;
};

export type BulkNodeInput = {
	name: string;
	sortOrder: number;
	type: "Sum" | "Group";
	functionCode?: string | null;
	formulas?: BulkFormulaInput[];
	nodes?: BulkNodeInput[];
};

export type BulkHierarchyInput = {
	name: string;
	reportingType: "IncomeStatement" | "BalanceSheet";
	validationFromDate: string;
	nodes?: BulkNodeInput[];
};

export type BreakdownGroupV2 = {
	id: string;
	name: string;
	parentId: string | null;
	sortOrder: number;
	type: "Sum" | "Group";
	functionCode: string | null;
	amount: number;
	rawAmount: number;
	nodes: BreakdownGroupV2[];
};

export type BreakdownV2Result = {
	hierarchyId: string;
	from: string;
	to: string;
	nodes: BreakdownGroupV2[];
	uncoveredAmount: number;
};

export type TemplateV2ListItem = {
	id: string;
	name: string;
	description: string | null;
	reportingType: "IncomeStatement" | "BalanceSheet";
	scope: "Global" | "CompanyDomain" | "LegalEntity";
	createdAt: string;
	updatedAt: string;
};

export type TemplateFormulaResponse = {
	id: string;
	accountFormula: string;
	costCenterFormula: string | null;
	sortOrder: number;
};

export type TemplateGroupResponse = {
	id: string;
	name: string;
	parentId: string | null;
	sortOrder: number;
	type: "Sum" | "Group";
	functionCode: string | null;
	formulas: TemplateFormulaResponse[];
	nodes: TemplateGroupResponse[];
};

export type TemplateV2Detail = TemplateV2ListItem & {
	nodes: TemplateGroupResponse[];
};

export type GroupedAccountItem = {
	accountNumber: string;
	accountName: string;
	costCenterCode: string | null;
	costCenterName: string | null;
	amount: number;
};

export type ReportGroupItem = {
	groupId: string;
	groupName: string;
	amount: number;
	rawAmount: number;
};

export type ReportV2ListItem = {
	id: string;
	hierarchyId: string;
	legalEntityId: string;
	periodFrom: string;
	periodTo: string;
	computedAt: string;
};

export type ReportV2Detail = ReportV2ListItem & {
	groups: ReportGroupItem[];
};

type BaseParams = {
	companyDomainId: string;
	legalEntityId: string;
	hierarchyId: string;
};

type GroupParams = BaseParams & { groupId: string };

type FormulaParams = GroupParams & { formulaId: string };

const hierarchyV2Url = (
	companyDomainId: string,
	legalEntityId: string,
	hierarchyId: string,
) =>
	`/v1/company-domain/${companyDomainId}/legal-entity/${legalEntityId}/hierarchy-v2/${hierarchyId}`;

const legalEntityUrl = (companyDomainId: string, legalEntityId: string) =>
	`/v1/company-domain/${companyDomainId}/legal-entity/${legalEntityId}/hierarchy-v2`;

const detailTag = (hierarchyId: string) => ({
	type: "AccountHierarchyV2" as const,
	id: hierarchyId,
});

export const buildHierarchyV2Endpoints = (builder: Builder) => ({
	getAccountHierarchiesV2: builder.query<
		HierarchyV2ListItem[],
		{ companyDomainId: string; legalEntityId: string }
	>({
		query: ({ companyDomainId, legalEntityId }) => ({
			url: `/v1/company-domain/${companyDomainId}/legal-entity/${legalEntityId}/hierarchy-v2`,
		}),
		providesTags: ["AccountHierarchyV2"],
	}),

	getAccountHierarchyV2: builder.query<
		HierarchyV2Detail,
		{ companyDomainId: string; legalEntityId: string; hierarchyId: string }
	>({
		query: ({ companyDomainId, legalEntityId, hierarchyId }) => ({
			url: hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId),
		}),
		providesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	applyHierarchyV2Batch: builder.mutation<
		HierarchyBatchResponse,
		BaseParams & { body: { operations: HierarchyBatchOperation[] } }
	>({
		query: ({ companyDomainId, legalEntityId, hierarchyId, body }) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/batch`,
			method: "POST",
			body,
		}),
		invalidatesTags: (_result, _error, arg) => [
			detailTag(arg.hierarchyId),
			"AccountHierarchyV2",
		],
	}),

	revertHierarchyV2ToVersion: builder.mutation<
		HierarchyV2Detail,
		BaseParams & { versionNumber: number }
	>({
		query: ({
			companyDomainId,
			legalEntityId,
			hierarchyId,
			versionNumber,
		}) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/versions/${versionNumber}/revert`,
			method: "POST",
		}),
		invalidatesTags: (_result, _error, arg) => [
			detailTag(arg.hierarchyId),
			"AccountHierarchyV2",
		],
	}),

	getHierarchyV2Versions: builder.query<
		HierarchyVersionsResponse,
		{
			companyDomainId: string;
			legalEntityId: string;
			hierarchyId: string;
			take?: number;
			cursor?: string | null;
		}
	>({
		query: ({ companyDomainId, legalEntityId, hierarchyId, take, cursor }) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/versions`,
			params: {
				include: "summary",
				...(take === undefined ? {} : { take }),
				...(cursor ? { cursor } : {}),
			},
		}),
		providesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	getHierarchyV2Breakdown: builder.query<
		BreakdownV2Result,
		{
			companyDomainId: string;
			legalEntityId: string;
			hierarchyId: string;
			from: string;
			to: string;
		}
	>({
		query: ({ companyDomainId, legalEntityId, hierarchyId, from, to }) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/breakdown`,
			params: { from, to },
		}),
		providesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	createAccountHierarchyV2: builder.mutation<
		HierarchyV2ListItem,
		{
			companyDomainId: string;
			legalEntityId: string;
			body: {
				name: string;
				reportingType: "IncomeStatement" | "BalanceSheet";
				validationFromDate: string;
			};
		}
	>({
		query: ({ companyDomainId, legalEntityId, body }) => ({
			url: `/v1/company-domain/${companyDomainId}/legal-entity/${legalEntityId}/hierarchy-v2`,
			method: "POST",
			body,
		}),
		invalidatesTags: ["AccountHierarchyV2"],
	}),

	bulkCreateAccountHierarchyV2: builder.mutation<
		HierarchyV2Detail[],
		{
			companyDomainId: string;
			legalEntityId: string;
			body: BulkHierarchyInput[];
		}
	>({
		query: ({ companyDomainId, legalEntityId, body }) => ({
			url: `/v1/company-domain/${companyDomainId}/legal-entity/${legalEntityId}/hierarchy-v2/bulk`,
			method: "POST",
			body,
		}),
		invalidatesTags: ["AccountHierarchyV2"],
	}),

	publishAccountHierarchyV2: builder.mutation<HierarchyV2Detail, BaseParams>({
		query: ({ companyDomainId, legalEntityId, hierarchyId }) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/publish`,
			method: "POST",
		}),
		invalidatesTags: (_result, _error, arg) => [
			"AccountHierarchyV2",
			detailTag(arg.hierarchyId),
		],
	}),

	updateAccountHierarchyV2: builder.mutation<
		HierarchyV2Detail,
		BaseParams & { body: { name?: string; excludedAccountNumbers?: string[] } }
	>({
		query: ({ companyDomainId, legalEntityId, hierarchyId, body }) => ({
			url: hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId),
			method: "PATCH",
			body,
		}),
		invalidatesTags: (_result, _error, arg) => [
			"AccountHierarchyV2",
			detailTag(arg.hierarchyId),
		],
	}),

	deleteAccountHierarchyV2: builder.mutation<void, BaseParams>({
		query: ({ companyDomainId, legalEntityId, hierarchyId }) => ({
			url: hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId),
			method: "DELETE",
		}),
		invalidatesTags: ["AccountHierarchyV2"],
	}),

	createGroupV2: builder.mutation<
		GroupV2Response,
		BaseParams & {
			body: {
				name: string;
				sortOrder: number;
				type: "Sum" | "Group";
				parentId?: string | null;
				functionCode?: string | null;
			};
		}
	>({
		query: ({ companyDomainId, legalEntityId, hierarchyId, body }) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/groups`,
			method: "POST",
			body,
		}),
		invalidatesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	updateGroupV2: builder.mutation<
		GroupV2Response,
		GroupParams & {
			body: {
				name?: string;
				sortOrder?: number;
				type?: "Sum" | "Group";
				functionCode?: string | null;
			};
		}
	>({
		query: ({
			companyDomainId,
			legalEntityId,
			hierarchyId,
			groupId,
			body,
		}) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/groups/${groupId}`,
			method: "PATCH",
			body,
		}),
		invalidatesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	deleteGroupV2: builder.mutation<void, GroupParams>({
		query: ({ companyDomainId, legalEntityId, hierarchyId, groupId }) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/groups/${groupId}`,
			method: "DELETE",
		}),
		invalidatesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	moveGroupV2: builder.mutation<
		void,
		GroupParams & { body: { parentId: string | null; sortOrder: number } }
	>({
		query: ({
			companyDomainId,
			legalEntityId,
			hierarchyId,
			groupId,
			body,
		}) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/groups/${groupId}/move`,
			method: "POST",
			body,
		}),
		invalidatesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	createFormulaV2: builder.mutation<
		FormulaV2Response,
		GroupParams & {
			body: {
				accountFormula: string;
				costCenterFormula?: string | null;
				sortOrder: number;
			};
		}
	>({
		query: ({
			companyDomainId,
			legalEntityId,
			hierarchyId,
			groupId,
			body,
		}) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/groups/${groupId}/formulas`,
			method: "POST",
			body,
		}),
		invalidatesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	updateFormulaV2: builder.mutation<
		FormulaV2Response,
		FormulaParams & {
			body: {
				accountFormula?: string;
				costCenterFormula?: string | null;
				sortOrder?: number;
			};
		}
	>({
		query: ({
			companyDomainId,
			legalEntityId,
			hierarchyId,
			groupId,
			formulaId,
			body,
		}) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/groups/${groupId}/formulas/${formulaId}`,
			method: "PATCH",
			body,
		}),
		invalidatesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	deleteFormulaV2: builder.mutation<void, FormulaParams>({
		query: ({
			companyDomainId,
			legalEntityId,
			hierarchyId,
			groupId,
			formulaId,
		}) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/groups/${groupId}/formulas/${formulaId}`,
			method: "DELETE",
		}),
		invalidatesTags: (_result, _error, arg) => [detailTag(arg.hierarchyId)],
	}),

	getTemplatesV2: builder.query<
		TemplateV2ListItem[],
		{ companyDomainId: string; legalEntityId: string }
	>({
		query: ({ companyDomainId, legalEntityId }) => ({
			url: `${legalEntityUrl(companyDomainId, legalEntityId)}/templates`,
		}),
		providesTags: ["HierarchyTemplateV2"],
	}),

	getTemplateV2: builder.query<
		TemplateV2Detail,
		{ companyDomainId: string; legalEntityId: string; templateId: string }
	>({
		query: ({ companyDomainId, legalEntityId, templateId }) => ({
			url: `${legalEntityUrl(companyDomainId, legalEntityId)}/templates/${templateId}`,
		}),
		providesTags: (_r, _e, arg) => [
			{ type: "HierarchyTemplateV2" as const, id: arg.templateId },
		],
	}),

	createTemplateV2: builder.mutation<
		TemplateV2Detail,
		{
			companyDomainId: string;
			legalEntityId: string;
			body: {
				name: string;
				description?: string;
				reportingType: "IncomeStatement" | "BalanceSheet";
				scope: "CompanyDomain" | "LegalEntity";
			};
		}
	>({
		query: ({ companyDomainId, legalEntityId, body }) => ({
			url: `${legalEntityUrl(companyDomainId, legalEntityId)}/templates`,
			method: "POST",
			body,
		}),
		invalidatesTags: ["HierarchyTemplateV2"],
	}),

	updateTemplateV2: builder.mutation<
		TemplateV2Detail,
		{
			companyDomainId: string;
			legalEntityId: string;
			templateId: string;
			body: { name?: string; description?: string | null };
		}
	>({
		query: ({ companyDomainId, legalEntityId, templateId, body }) => ({
			url: `${legalEntityUrl(companyDomainId, legalEntityId)}/templates/${templateId}`,
			method: "PATCH",
			body,
		}),
		invalidatesTags: ["HierarchyTemplateV2"],
	}),

	deleteTemplateV2: builder.mutation<
		void,
		{ companyDomainId: string; legalEntityId: string; templateId: string }
	>({
		query: ({ companyDomainId, legalEntityId, templateId }) => ({
			url: `${legalEntityUrl(companyDomainId, legalEntityId)}/templates/${templateId}`,
			method: "DELETE",
		}),
		invalidatesTags: ["HierarchyTemplateV2"],
	}),

	instantiateTemplateV2: builder.mutation<
		HierarchyV2Detail,
		{
			companyDomainId: string;
			legalEntityId: string;
			templateId: string;
			body: { name: string };
		}
	>({
		query: ({ companyDomainId, legalEntityId, templateId, body }) => ({
			url: `${legalEntityUrl(companyDomainId, legalEntityId)}/templates/${templateId}/instantiate`,
			method: "POST",
			body,
		}),
		invalidatesTags: ["AccountHierarchyV2"],
	}),

	getGroupedAccountsV2: builder.query<
		GroupedAccountItem[],
		{
			companyDomainId: string;
			legalEntityId: string;
			from: string;
			to: string;
		}
	>({
		query: ({ companyDomainId, legalEntityId, from, to }) => ({
			url: `${legalEntityUrl(companyDomainId, legalEntityId)}/grouped-accounts`,
			params: { from, to },
		}),
	}),

	getReportsV2: builder.query<
		ReportV2ListItem[],
		BaseParams & { periodFrom?: string; periodTo?: string }
	>({
		query: ({
			companyDomainId,
			legalEntityId,
			hierarchyId,
			periodFrom,
			periodTo,
		}) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/reports`,
			params: { periodFrom, periodTo },
		}),
		providesTags: (_r, _e, arg) => [
			{ type: "HierarchyReportV2" as const, id: arg.hierarchyId },
		],
	}),

	getReportV2: builder.query<ReportV2Detail, BaseParams & { reportId: string }>(
		{
			query: ({ companyDomainId, legalEntityId, hierarchyId, reportId }) => ({
				url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/reports/${reportId}`,
			}),
		},
	),

	createReportV2: builder.mutation<
		ReportV2Detail,
		BaseParams & { body: { periodFrom: string; periodTo: string } }
	>({
		query: ({ companyDomainId, legalEntityId, hierarchyId, body }) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/reports`,
			method: "POST",
			body,
		}),
		invalidatesTags: (_r, _e, arg) => [
			{ type: "HierarchyReportV2" as const, id: arg.hierarchyId },
		],
	}),

	deleteReportV2: builder.mutation<void, BaseParams & { reportId: string }>({
		query: ({ companyDomainId, legalEntityId, hierarchyId, reportId }) => ({
			url: `${hierarchyV2Url(companyDomainId, legalEntityId, hierarchyId)}/reports/${reportId}`,
			method: "DELETE",
		}),
		invalidatesTags: (_r, _e, arg) => [
			{ type: "HierarchyReportV2" as const, id: arg.hierarchyId },
		],
	}),
});
